import { useState } from 'react';
import { Award, ChevronDown } from 'lucide-react';
import { Card, CardContent, cn } from '@mlm/ui';
import { apiPathUrl, type RankProgress, type RankRequirementKey } from '../../lib/api';
import { fmtCurrency, fmtNumber } from '../../lib/format';
import { overallRankProgressPct, requirementPct } from '../../lib/rank-progress';

type Requirement = {
  key: RankRequirementKey;
  label: string;
  have: number;
  need: number;
  fmt: (n: number) => string;
  /** "X more to go" copy for an unmet bar. */
  remaining: (n: number) => string;
};

function Bar({ req }: { req: Requirement }) {
  const { have, need, fmt } = req;
  const p = requirementPct(have, need);
  const met = have >= need;
  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-caption text-muted-foreground">{req.label}</span>
        <span
          className={`text-caption tabular-nums ${met ? 'text-emerald-600' : 'text-foreground'}`}
        >
          {fmt(have)} / {fmt(need)} · {Math.floor(p)}%
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full ${met ? 'bg-emerald-500' : 'bg-primary'}`}
          style={{ width: `${p}%` }}
        />
      </div>
      {!met && (
        <p className="mt-1 text-[11px] text-muted-foreground">{req.remaining(need - have)} to go</p>
      )}
    </div>
  );
}

/** Rank icon from its API path, falling back to a lucide Award. */
function RankBadge({ iconUrl }: { iconUrl: string | null }) {
  const [failed, setFailed] = useState(false);
  if (iconUrl && !failed) {
    return (
      <img
        src={apiPathUrl(iconUrl)}
        alt=""
        className="h-10 w-10 shrink-0 object-contain"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
      <Award className="h-5 w-5" />
    </span>
  );
}

/**
 * "Progress to <next rank>" with an overall percentage (the mean of the
 * VISIBLE bars, see `overallRankProgressPct`) — shows the slot's current rank
 * and how close it is to the next one. Requirements set to 0 or hidden by the admin
 * (`trackerHidden`) are dropped; with none left only the header shows.
 * Rendered only when stairstep is active.
 */
export function RankProgressPanel({ progress }: { progress: RankProgress }) {
  const [aboutOpen, setAboutOpen] = useState(false);
  const next = progress.nextRank;
  const currentName = progress.currentRankName ?? progress.currentRankCode ?? 'Unranked';

  if (!next) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-caption font-medium uppercase tracking-[0.1em] text-muted-foreground">
            Rank progress
          </p>
          <div className="mt-3 flex items-center gap-3">
            <RankBadge key={progress.currentRankIconUrl} iconUrl={progress.currentRankIconUrl} />
            <p className="text-body">
              <span className="font-semibold capitalize">{currentName}</span> — you're at the top
              rank.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const hidden = new Set(next.trackerHidden ?? []);
  const reqs: Requirement[] = (
    [
      {
        key: 'ppv',
        label: 'Personal PV',
        have: Number(progress.currentPpv),
        need: Number(next.minPpv),
        fmt: fmtNumber,
        remaining: (n) => `${fmtNumber(n)} more PV`,
      },
      {
        key: 'gpv',
        label: 'Group PV',
        have: Number(progress.currentGpv),
        need: Number(next.minGpv),
        fmt: fmtNumber,
        remaining: (n) => `${fmtNumber(n)} more group PV`,
      },
      {
        key: 'legs',
        label: next.minLegRank ? `Legs (rank ${next.minLegRank}+)` : 'Qualifying legs',
        have: next.qualifyingLegs,
        need: next.minLegs,
        fmt: fmtNumber,
        remaining: (n) => `${fmtNumber(n)} more ${n === 1 ? 'leg' : 'legs'}`,
      },
      {
        key: 'directs',
        label: 'Active directs',
        have: progress.activeDirects,
        need: next.minDirects,
        fmt: fmtNumber,
        remaining: (n) => `${fmtNumber(n)} more ${n === 1 ? 'direct' : 'directs'}`,
      },
      {
        key: 'total_earned',
        label: 'Total Earned',
        have: Number(progress.currentTotalEarned),
        need: Number(next.minTotalEarned),
        fmt: (n) => fmtCurrency(n),
        remaining: (n) => `${fmtCurrency(n)} more`,
      },
    ] satisfies Requirement[]
  ).filter((r) => Number.isFinite(r.need) && r.need > 0 && !hidden.has(r.key));

  const nextName = next.name ?? next.code;
  const overall = overallRankProgressPct(reqs);

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center gap-3">
          <RankBadge key={next.iconUrl} iconUrl={next.iconUrl} />
          <div className="min-w-0 flex-1">
            <p className="text-caption font-medium uppercase tracking-[0.1em] text-muted-foreground">
              Progress to <span className="capitalize">{nextName}</span>
            </p>
            <p className="mt-1 text-caption text-muted-foreground">
              Current rank: <span className="capitalize text-foreground">{currentName}</span>
            </p>
          </div>
          {overall != null && (
            <div className="shrink-0 text-right" aria-label={`${overall}% to ${nextName}`}>
              <p
                className={cn(
                  'text-2xl font-semibold leading-none tabular-nums',
                  overall >= 100 ? 'text-emerald-600' : 'text-foreground',
                )}
              >
                {overall}%
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">to rank up</p>
            </div>
          )}
        </div>
        {overall != null && (
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                'h-full rounded-full',
                overall >= 100 ? 'bg-emerald-500' : 'bg-primary',
              )}
              style={{ width: `${overall}%` }}
            />
          </div>
        )}
        {reqs.length > 0 && (
          <div className="mt-3 space-y-3">
            {reqs.map((r) => (
              <Bar key={r.key} req={r} />
            ))}
          </div>
        )}
        {next.description && (
          <div className="mt-4 border-t pt-3">
            <button
              type="button"
              onClick={() => setAboutOpen((v) => !v)}
              aria-expanded={aboutOpen}
              className="flex w-full items-center justify-between text-caption font-medium text-foreground"
            >
              About this rank
              <ChevronDown
                className={cn('h-4 w-4 transition-transform', aboutOpen && 'rotate-180')}
              />
            </button>
            {aboutOpen && (
              <p className="mt-2 whitespace-pre-line text-caption text-muted-foreground">
                {next.description}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
